import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReportToggle from '@/components/test/ReportToggle';
import EvaluatorsTest from '@/components/test/EvaluatorsTest';
import AutomationsTest from '@/components/test/AutomationsTest';
import AdvancedPlansDataTester from '@/components/test/AdvancedPlansDataTester';
import ReportDataTester from '@/components/test/ReportDataTester';
import ExampleAdvancedPlansDataTester from '@/components/test/ExampleAdvancedPlansDataTester';
import ExampleReportDataTester from '@/components/test/ExampleReportDataTester';
import PlansTest from '@/components/test/PlansTest';
import TestPageHeader from '@/components/test/layout/TestPageHeader';
import { useEvaluators } from '@/components/test/hooks/useEvaluators';
import { toggleEvaluatorStatus } from '@/components/test/utils/evaluatorUtils';
import Header from '../components/Header';

const evaluatorsDetailData = {
  __metadata: {
    immutableFields: [
      'evaluators',
      'evaluators.*.name',
      'evaluators.*.role',
      'evaluators.*.sections',
      'evaluators.*.sections.*.title',
      'evaluators.*.sections.*.content'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  evaluators: [
    {
      name: "Sophia Carter",
      role: "Peer",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "How do you think we could improve how we collaborate on cross-functional projects?"
            },
            {
              subtitle: "Tip",
              text: "Sophia prefers concise and actionable updates during meetings; prepare clear bullet points for discussions."
            },
            {
              subtitle: "Listening Practice",
              text: "Repeat back Sophia's key points to confirm understanding and show engagement."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Consider Sophia's workload during project deadlines and offer support when appropriate."
            },
            {
              subtitle: "Trust Strategy",
              text: "Deliver on promises, such as meeting deadlines for shared tasks."
            },
            {
              subtitle: "Recognition Practice",
              text: "Publicly acknowledge Sophia's contributions during team retrospectives."
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "What's one thing I could adjust to make our collaboration smoother?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Address disagreements constructively, e.g., \"Let's explore a middle ground for our project timeline.\""
            },
            {
              subtitle: "Reflective Prompt",
              text: "Did I effectively address Sophia's concerns during our last meeting?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Collaborate to streamline project workflows."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Sophia's organizational skills can help in creating structured project plans."
            },
            {
              subtitle: "Balance Focus",
              text: "Encourage Sophia to pair strategic planning with actionable next steps."
            }
          ]
        }
      ]
    },
    {
      name: "Liam Johnson",
      role: "Line Manager",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "Are there ways I could make updates more efficient for you?"
            },
            {
              subtitle: "Tip",
              text: "Liam values proactive updates; send a brief summary of progress before formal reviews."
            },
            {
              subtitle: "Listening Practice",
              text: "Take notes during discussions and follow up on action items."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Show understanding of Liam's strategic vision by aligning your efforts with organizational priorities."
            },
            {
              subtitle: "Trust Strategy",
              text: "Be transparent about challenges and provide realistic solutions during check-ins."
            },
            {
              subtitle: "Recognition Practice",
              text: "Thank Liam for mentoring opportunities, e.g., \"Your feedback during my presentation preparation was invaluable.\""
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "What's one skill you think I should develop to contribute more effectively to the team?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Approach disagreements by focusing on shared organizational objectives."
            },
            {
              subtitle: "Reflective Prompt",
              text: "Am I presenting my ideas in a way that aligns with Liam's expectations?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Support the department's strategic initiatives through improved project outcomes."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Liam's big-picture thinking can inspire innovative approaches to challenges."
            },
            {
              subtitle: "Balance Focus",
              text: "Translate Liam's strategic vision into actionable steps for your team."
            }
          ]
        }
      ]
    },
    {
      name: "Ava Martinez",
      role: "Direct Report",
      sections: [
        {
          title: "Communication and Collaboration Strategies",
          content: [
            {
              subtitle: "Conversation Starter",
              text: "How can I support you better in achieving your goals?"
            },
            {
              subtitle: "Tip",
              text: "Ava appreciates regular check-ins; schedule bi-weekly one-on-one meetings to discuss her progress."
            },
            {
              subtitle: "Listening Practice",
              text: "Create a safe space for Ava to share challenges by actively listening and asking open-ended questions."
            }
          ]
        },
        {
          title: "Building Trust and Strengthening Relationships",
          content: [
            {
              subtitle: "Empathy Builder",
              text: "Understand Ava's career aspirations and provide growth opportunities."
            },
            {
              subtitle: "Trust Strategy",
              text: "Delegate tasks with clear expectations and provide constructive feedback."
            },
            {
              subtitle: "Recognition Practice",
              text: "Highlight Ava's contributions during team meetings, e.g., \"Ava's creativity was key to this project's success.\""
            }
          ]
        },
        {
          title: "Constructive Feedback and Conflict Resolution",
          content: [
            {
              subtitle: "Feedback Request",
              text: "Is there anything I could do differently to better support your work?"
            },
            {
              subtitle: "Conflict Technique",
              text: "Address misunderstandings promptly and focus on resolving issues collaboratively."
            },
            {
              subtitle: "Reflective Prompt",
              text: "Am I providing Ava with enough guidance and support without micromanaging?"
            }
          ]
        },
        {
          title: "Leveraging Strengths and Shared Goals",
          content: [
            {
              subtitle: "Shared Goal",
              text: "Ensure project deadlines are met while fostering Ava's professional development."
            },
            {
              subtitle: "Strength Amplifier",
              text: "Ava's creativity can be leveraged for brainstorming innovative solutions."
            },
            {
              subtitle: "Balance Focus",
              text: "Help Ava align her individual goals with the team's objectives."
            }
          ]
        }
      ]
    }
  ]
};

const evaluatorsCoherenceData = {
  __metadata: {
    immutableFields: [
      'score',
      'description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  score: 87,
  description: 'High consistency among evaluators\' responses, indicating reliable feedback'
};

const evaluatorsSentimentData = {
  __metadata: {
    immutableFields: [
      'score',
      'description'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  score: 35,
  description: 'Overall positive sentiment from evaluators\' feedback'
};

const softHardSkillsData = {
  __metadata: {
    immutableFields: [
      'introText',
      'softSkills',
      'hardSkills',
      'detailedFeedback',
      'actionableRecommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  introText: "Michael, your technical expertise and problem-solving abilities stand out as key strengths, showcasing your capacity to deliver high-quality results in challenging situations. These hard skills set you apart as a dependable professional. On the other hand, your leadership and communication skills represent areas for growth. At times, your assertive tone may come across as pushy, and enhancing your ability to inspire and motivate others will elevate your leadership impact. By refining these interpersonal skills, you'll unlock your full potential to foster stronger connections, lead more effectively, and create a balanced professional profile of technical and relational excellence.",
  softSkills: {
    selfScore: 3.5,
    evaluatorScore: 2.8
  },
  hardSkills: {
    selfScore: 4.0,
    evaluatorScore: 4.2
  },
  detailedFeedback: [
    "Your hard skills, particularly technical expertise and problem-solving, are standout strengths. You consistently deliver high-quality results and effectively handle complex challenges. This establishes you as a dependable and skilled professional.",
    "Soft skills, while adequate in some areas, have room for development to balance your technical strengths. Evaluators noted opportunities to refine interpersonal dynamics, inspire your team more effectively, and ensure a collaborative tone in your interactions."
  ],
  actionableRecommendations: [
    "Continue leveraging your technical strengths by mentoring peers or sharing best practices to enhance team performance.",
    "Actively involve others in problem-solving processes to foster collaborative solutions and diverse perspectives.",
    "Focus on building rapport and strengthening relationships by fostering empathy and active listening in conversations.",
    "Participate in training sessions or workshops that enhance interpersonal dynamics, emotional intelligence, and team motivation.",
    "Develop strategies to balance assertiveness with inclusivity in communication and leadership scenarios."
  ]
};

const skillsDetailData = {
  __metadata: {
    immutableFields: [
      'dimensions',
      'dimension.name',
      'dimension.tag',
      'dimension.summary',
      'dimension.selfScore',
      'dimension.evaluatorScore',
      'dimension.feedback',
      'dimension.specificExamples',
      'dimension.actionableRecommendations'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  dimensions: [
    {
      name: "Communication",
      tag: "Blind Spot",
      summary: "Your communication is clear and effective, but evaluators noted occasional assertiveness that may come across as pushy. Refining your tone and delivery could enhance message receptiveness.",
      selfScore: 3.5,
      evaluatorScore: 2.0,
      feedback: [
        "Your clarity in presenting ideas is highly appreciated.",
        "Evaluators suggest toning down assertiveness for smoother interactions.",
        "A focus on active listening can further improve outcomes."
      ],
      specificExamples: "Evaluators noted that your communication often appears clear and goal-oriented. However, a few instances highlighted a perceived lack of openness to feedback, which could hinder collaboration.",
      actionableRecommendations: [
        "Work on creating two-way communication channels by actively asking for input and feedback during discussions.",
        "Engage in active listening exercises to balance assertiveness with inclusivity.",
        "Consider communication coaching sessions to refine tone and messaging, ensuring clarity without being perceived as overly assertive."
      ]
    },
    {
      name: "Collaboration",
      tag: "Key Strength",
      summary: "You are an excellent team player who fosters collaboration and harmony. Peers value your approachability and ability to adapt to group dynamics.",
      selfScore: 4.5,
      evaluatorScore: 4.6,
      feedback: [
        "Peers feel supported by your inclusive approach.",
        "Your adaptability in group tasks is highly valued.",
        "Continue to foster open communication within teams for maximum impact."
      ],
      specificExamples: "Your ability to foster collaboration is highly appreciated, as seen in a recent cross-departmental project where you encouraged all team members to contribute ideas. Peers highlighted your approachability and adaptability when resolving inter-team challenges.",
      actionableRecommendations: [
        "Continue building strong relationships by regularly seeking input from quieter team members to ensure inclusivity.",
        "Take the lead in organizing team-building activities to further enhance rapport among colleagues.",
        "Leverage collaboration tools to streamline communication and task management for group projects."
      ]
    },
    {
      name: "Initiative",
      tag: "Hidden Strength",
      summary: "Your proactive nature and willingness to take charge are seen as strengths. While evaluators align with your view, focusing on balancing ambition with team dynamics can be beneficial.",
      selfScore: 3.0,
      evaluatorScore: 4.0,
      feedback: [
        "Your initiative drives projects forward efficiently.",
        "Some evaluators suggest ensuring alignment with team priorities.",
        "Leverage your initiative to inspire colleagues further."
      ],
      specificExamples: "Your proactive approach was evident during a recent project where you stepped up to manage a potential risk. However, evaluators suggest balancing proactivity with seeking input to avoid overwhelming team dynamics.",
      actionableRecommendations: [
        "Set clear boundaries for your initiatives to ensure alignment with team objectives and priorities.",
        "Take a coaching or mentoring role to inspire colleagues and promote shared ownership of proactive decisions.",
        "Reflect on past actions to assess where collaboration could have been further integrated into your initiatives."
      ]
    },
    {
      name: "Problem-Solving",
      tag: "",
      summary: "Problem-solving is one of your standout strengths, with high scores across both self-assessment and evaluators. Your analytical approach and decisiveness are admired.",
      selfScore: 4.6,
      evaluatorScore: 4.5,
      feedback: [
        "Your ability to analyze and resolve complex issues is highly respected.",
        "Peers appreciate your decisive action during challenges.",
        "Enhancing collaboration during problem-solving can create broader solutions."
      ],
      specificExamples: "Evaluators commended your analytical skills, especially during high-stakes situations like resolving technical bottlenecks in client presentations. However, there's an opportunity to further involve your team in brainstorming solutions for more comprehensive outcomes.",
      actionableRecommendations: [
        "Facilitate problem-solving workshops to encourage collective team input and share your decision-making process.",
        "Document successful resolutions and share lessons learned as best practices for future challenges.",
        "Attend advanced problem-solving or strategic thinking courses to further refine your approach."
      ]
    },
    {
      name: "Leadership",
      tag: "Blind Spot",
      summary: "Leadership development remains an area for growth. Evaluators note a need to inspire and motivate team members, aligning with your own recognition of this opportunity.",
      selfScore: 3.0,
      evaluatorScore: 1.8,
      feedback: [
        "Peers feel your leadership can be more empowering.",
        "Focus on fostering a motivational environment for team members.",
        "Develop strategies to align team goals with broader organizational objectives."
      ],
      specificExamples: "Leadership emerged as an area for improvement, with evaluators noting that more motivational engagement with your team is needed. For example, team members mentioned a lack of emotional connection when goals are communicated.",
      actionableRecommendations: [
        "Focus on motivational techniques, such as public recognition of team achievements and creating a shared vision for projects.",
        "Participate in leadership development programs to enhance skills in inspiring and empowering your team.",
        "Build stronger relationships with team members through one-on-one meetings to understand their unique goals and challenges."
      ]
    },
    {
      name: "Performance",
      tag: "Confirmed Strength",
      summary: "Consistently high performance defines your professional contributions. Both you and evaluators agree on your ability to deliver high-quality results under pressure.",
      selfScore: 4.7,
      evaluatorScore: 4.8,
      feedback: [
        "Your consistent delivery of results is highly valued by peers.",
        "Maintain focus on performance quality while ensuring team alignment.",
        "Consider sharing best practices to uplift overall team performance."
      ],
      specificExamples: "Both evaluators and self-assessment align in recognizing your consistent delivery of high-quality work, even under pressure. However, there were mentions of a tendency to focus excessively on perfection, potentially slowing task delegation.",
      actionableRecommendations: [
        "Develop time-management strategies to ensure timely task completion while maintaining quality.",
        "Delegate more effectively by creating clear guidelines or SOPs for repetitive tasks.",
        "Engage in performance optimization workshops to learn how to balance perfectionism with productivity."
      ]
    }
  ]
};

const radarChartData = {
  __metadata: {
    immutableFields: [
      'radarChart',
      'radarChart.*.user',
      'radarChart.*.evaluators'
    ],
    version: '1.0',
    lastUpdated: 'auto-generated'
  },
  radarChart: {
    Communication: { user: 3.5, evaluators: 2.0 },
    Collaboration: { user: 4.5, evaluators: 4.6 },
    Initiative: { user: 3.0, evaluators: 4.0 },
    "Problem-Solving": { user: 4.6, evaluators: 4.5 },
    Leadership: { user: 3.0, evaluators: 1.8 },
    Performance: { user: 4.7, evaluators: 4.8 }
  }
};

export default function TestPage() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { evaluators, setEvaluators, loading, error } = useEvaluators();
  const [updating, setUpdating] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleToggleStatus = async (evaluatorId: string, field: 'evaluationCompleted' | 'contextualized') => {
    if (!currentUser) return;

    try {
      setUpdating(evaluatorId);
      const evaluator = evaluators.find(e => e.id === evaluatorId);
      if (!evaluator) return;

      await toggleEvaluatorStatus(evaluatorId, currentUser.uid, field, evaluator[field]);

      setEvaluators(prev => prev.map(e => 
        e.id === evaluatorId 
          ? { ...e, [field]: !e[field] }
          : e
      ));
    } catch (error) {
      console.error(`Error updating evaluator ${field}:`, error);
    } finally {
      setUpdating(null);
    }
  };

  const sections = [
    {
      title: "Plans Test",
      content: <PlansTest />
    },
    {
      title: "Report Data Test",
      content: <ReportDataTester />
    },
    {
      title: "Advanced Plans Data Test",
      content: <AdvancedPlansDataTester />
    },
    {
      title: "Report Availability Test",
      content: <ReportToggle />
    },
    {
      title: "Evaluators Test",
      content: (
        <EvaluatorsTest
          evaluators={evaluators}
          updating={updating}
          onToggleStatus={handleToggleStatus}
        />
      )
    },
    {
      title: "Automations Test",
      content: <AutomationsTest />
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        onLogout={handleLogout}
        onProfileClick={() => navigate('/profile')}
        userData={{ firstName: '', lastName: '' }}
      />
      
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TestPageHeader
            title="Testing Dashboard"
            description="Use this page to test and verify various system functionalities."
            error={error}
          />

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-6">{section.title}</h2>
                {section.content}
              </motion.div>
            ))}

            {/* Example Data Generation Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sections.length * 0.1 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Example Data Generation</h2>
              <div className="space-y-4">
                <ExampleReportDataTester />
                <ExampleAdvancedPlansDataTester />
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}